import { useState, useEffect } from "react";
import { V1BEClient } from "@/clients";
import {
  IOrganizationEventAttendee,
  IOrganizationEvent,
  IPaginatedOrganizationEventAttendees,
  OrganizationEventAttendeeStatus,
} from "@/types/EventTypes";
import {
  Users,
  Search,
  Filter,
  UserCheck,
  UserX,
  UserMinus,
  Clock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import Pagination from "@/components/ui/Pagination";
import Select from "@/components/ui/form/Select";
import Input from "@/components/ui/form/Input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { Calendar } from "lucide-react";
import { capitalize } from "@/lib/utils";

const EventAttendees = () => {
  const [attendeesData, setAttendeesData] =
    useState<IPaginatedOrganizationEventAttendees | null>(null);
  const [events, setEvents] = useState<IOrganizationEvent[]>([]);
  const [selectedUserForEvents, setSelectedUserForEvents] =
    useState<IOrganizationEventAttendee | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter states

  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedAttendingAs, setSelectedAttendingAs] = useState<string>("");
  const [searchName, setSearchName] = useState<string>("");
  const [searchEmail, setSearchEmail] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Sorting states
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Load organization events for the dropdown
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const eventsData = await V1BEClient.getOrganizationEvents();
        setEvents(eventsData);
      } catch (error) {
        console.error("Error loading organization events:", error);
      }
    };
    loadEvents();
  }, []);

  // Load attendees when filters or pagination changes
  useEffect(() => {
    loadAttendees();
  }, [
    selectedEventId,
    selectedStatus,
    selectedAttendingAs,
    searchTrigger,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
  ]);

  const loadAttendees = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (selectedEventId) params.organization_event_id = selectedEventId;

      if (selectedStatus) params.status = selectedStatus;
      if (selectedAttendingAs) params.attending_as = selectedAttendingAs;
      if (searchName) params.name = searchName;
      if (searchEmail) params.email = searchEmail;

      const data = await V1BEClient.getOrganizationEventAttendees(params);
      setAttendeesData(data);
    } catch (error) {
      console.error("Error loading attendees:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      // Set new column and default to DESC
      setSortBy(column);
      setSortOrder("DESC");
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ArrowUpDown size={16} className="text-gray-400" />;
    }
    return sortOrder === "ASC" ? (
      <ArrowUp size={16} className="text-primary-600" />
    ) : (
      <ArrowDown size={16} className="text-primary-600" />
    );
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleClearFilters = () => {
    setSelectedStatus("");
    setSelectedAttendingAs("");
    setSearchName("");
    setSearchEmail("");
    setCurrentPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const getStatusIcon = (status: OrganizationEventAttendeeStatus) => {
    switch (status) {
      case "attending":
        return <UserCheck className="text-green-600" size={20} />;
      case "not_attending":
        return <UserX className="text-red-600" size={20} />;
      case "maybe":
        return <UserMinus className="text-yellow-600" size={20} />;
      case "pending":
        return <Clock className="text-gray-600" size={20} />;
      default:
        return <Clock className="text-gray-600" size={20} />;
    }
  };

  const getStatusBadgeColor = (status: OrganizationEventAttendeeStatus) => {
    switch (status) {
      case "attending":
        return "bg-green-100 text-green-800";
      case "not_attending":
        return "bg-red-100 text-red-800";
      case "maybe":
        return "bg-yellow-100 text-yellow-800";
      case "pending":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatAttendingAs = (attendingAs: string) => {
    return attendingAs.charAt(0).toUpperCase() + attendingAs.slice(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalPages = attendeesData?.pages || 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Event Attendees
          </h1>
          <p className="text-gray-600">
            Manage and view event attendance records
          </p>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <Users size={24} />
          <span className="text-2xl font-semibold">
            {attendeesData?.total || 0}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Event Selector */}
          <div>
            <Select
              label="Event"
              placeholder="All Events"
              value={selectedEventId}
              onChange={(value) => {
                setSelectedEventId(value as string);
                setCurrentPage(1);
              }}
              options={[
                { value: "", label: "All Events" },
                ...events.map((event) => ({
                  value: event.id,
                  label: event.name,
                })),
              ]}
            />
          </div>

          {/* Status Filter */}
          <div>
            <Select
              label="Status"
              placeholder="All Statuses"
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value as string);
                setCurrentPage(1);
              }}
              options={[
                { value: "", label: "All Statuses" },
                { value: "attending", label: "Attending" },
                { value: "not_attending", label: "Not Attending" },
                { value: "maybe", label: "Maybe" },
                { value: "pending", label: "Pending" },
              ]}
            />
          </div>

          {/* Attending As Filter */}
          <div>
            <Select
              label="Attending As"
              placeholder="All Types"
              value={selectedAttendingAs}
              onChange={(value) => {
                setSelectedAttendingAs(value as string);
                setCurrentPage(1);
              }}
              options={[
                { value: "", label: "All Types" },
                { value: "guest", label: "Guest" },
                { value: "supplier", label: "Supplier" },
              ]}
            />
          </div>

          {/* User ID Search replaced with Name Search */}
          <div>
            <Input
              type="text"
              label="Name"
              placeholder="Search by Name"
              value={searchName}
              onChange={(e) => {
                setSearchName(e.target.value);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Email Search */}
          <div>
            <Input
              type="text"
              label="Email"
              placeholder="Search by Email"
              value={searchEmail}
              onChange={(e) => {
                setSearchEmail(e.target.value);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Search Button */}
          <div className="flex flex-col justify-end">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search size={20} />
              Search
            </button>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(selectedStatus ||
          selectedAttendingAs ||
          searchName ||
          searchEmail) && (
          <button
            onClick={handleClearFilters}
            className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Attendees Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading attendees...</p>
          </div>
        ) : attendeesData &&
          attendeesData.data &&
          attendeesData.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                      onClick={() => handleSort("first_name")}
                    >
                      <div className="flex items-center gap-2">
                        <span>User</span>
                        {getSortIcon("first_name")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Status</span>
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                      onClick={() => handleSort("attending_as")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Attending As</span>
                        {getSortIcon("attending_as")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                      <div className="flex items-center gap-2">
                        <span>Event(s)</span>
                        <Calendar size={16} className="text-gray-400" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                      onClick={() => handleSort("created_at")}
                    >
                      <div className="flex items-center gap-2">
                        <span>Registered At</span>
                        {getSortIcon("created_at")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendeesData.data.map((attendee) => (
                    <tr
                      key={attendee.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 font-semibold">
                              {attendee.first_name?.[0] || "U"}
                              {attendee.last_name?.[0] || ""}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {`${attendee.first_name} ${attendee.last_name}`}
                            </div>
                            <div className="text-sm text-gray-500">
                              {attendee.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(attendee.status)}
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(
                              attendee.status
                            )}`}
                          >
                            {formatStatus(attendee.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatAttendingAs(attendee.attending_as)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {attendee.user_events &&
                          attendee.user_events.length > 0 ? (
                            <button
                              onClick={() => setSelectedUserForEvents(attendee)}
                              className="text-left text-sm text-primary-600 hover:text-primary-700 hover:underline font-medium"
                            >
                              {attendee.user_events.length === 1
                                ? attendee.user_events[0].title
                                : `${attendee.user_events.length} Events`}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400 italic">
                              No events
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(
                          attendee.attendance_created_at || attendee.created_at
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={attendeesData.total}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <Users size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No attendees found
            </h3>
            <p className="text-gray-600">
              {selectedEventId
                ? "No attendees have registered for this event yet."
                : "No attendees have registered yet."}
            </p>
          </div>
        )}
      </div>

      {/* User Events Dialog */}
      <Dialog
        open={!!selectedUserForEvents}
        onOpenChange={(open) => !open && setSelectedUserForEvents(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>User Events</DialogTitle>
            <DialogDescription>
              Events created by{" "}
              <span className="font-medium text-gray-900">
                {selectedUserForEvents?.first_name}{" "}
                {selectedUserForEvents?.last_name}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedUserForEvents?.user_events &&
            selectedUserForEvents.user_events.length > 0 ? (
              <div className="space-y-4">
                {selectedUserForEvents.user_events.map((event, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="border-b border-gray-200 pb-2 mb-3">
                      <h4 className="font-semibold text-lg text-gray-900">
                        {event.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Calendar size={14} />
                        <span>{formatDate(event.event_date)}</span>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      {/* Celebrants */}
                      {event.celebrants &&
                        event.celebrants.filter((celebrant) => celebrant.id > 0)
                          .length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Celebrants:
                            </span>
                            <div className="ml-2 mt-1 flex flex-wrap gap-2">
                              {event.celebrants
                                .filter((celebrant) => celebrant.id > 0)
                                .map((celebrant, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-white border border-gray-200 rounded text-gray-600 text-xs"
                                  >
                                    {typeof celebrant === "string"
                                      ? celebrant
                                      : celebrant.name}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}

                      {/* String Properties */}
                      {event.event_string_properties &&
                        event.event_string_properties.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Details:
                            </span>
                            <div className="ml-2 mt-1 space-y-1">
                              {event.event_string_properties
                                .filter((prop) => prop.id > 0)
                                .map((prop, idx) => (
                                  <div
                                    key={idx}
                                    className="grid grid-cols-3 gap-2"
                                  >
                                    <span className="text-gray-500 capitalize col-span-1">
                                      {capitalize(prop.name.replace(/_/g, " "))}
                                      :
                                    </span>
                                    <span className="text-gray-800 col-span-2">
                                      {capitalize(prop.value)}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                      {/* Boolean Properties */}
                      {event.event_bool_properties &&
                        event.event_bool_properties.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">
                              Preferences:
                            </span>
                            <div className="ml-2 mt-1 space-y-1">
                              {event.event_bool_properties
                                .filter((prop) => prop.id > 0)
                                .map((prop, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-2"
                                  >
                                    <span className="text-gray-500 capitalize">
                                      {prop.name.replace(/_/g, " ")}:
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        prop.value
                                          ? "bg-green-100 text-green-700"
                                          : "bg-red-100 text-red-700"
                                      }`}
                                    >
                                      {prop.value ? "Yes" : "No"}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">
                No events found for this user.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventAttendees;
